import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { enableMapSet } from 'immer';
import { getAddress, Address } from 'viem';
import { devtools } from 'zustand/middleware';
import { persist, createJSONStorage, subscribeWithSelector } from 'zustand/middleware';
import { CURRENT_APP_VERSION, clearDataMigration, setAppVersion, BigIntDevtoolsSerialize } from '@/utils/zustand_helpers.ts';
import { environment } from '../environment.ts';

setAppVersion(environment.appVersion || '0.0.0');

import { createAuthSlice, AuthSlice, installAuthExpirySupervisor } from './slices/authSlice';
import { createLoadingSlice, LoadingSlice } from './slices/loadingSlice';
import { createAppConfigSlice, AppConfigSlice } from './slices/appConfigSlice';
import { createWalletSlice, DexWalletSlice } from './slices/dexWalletSlice.ts';
import { createMarketSlice, MarketSlice } from './slices/marketSlice.ts';
import { MarketData } from '@/common/MarketData.ts';
import { useOpenOrdersStore } from '../features/trading';
import { usePositionStore } from '@/feature/terminal_tables/position/PositionStore.ts';
import { useLiquidationAlertsStore } from '../features/account/liquidationAlertsStore';
import { useOrderHistoryStore } from '@/feature/terminal_tables/order_history/OrderHistoryStore.ts';
import { useAccountHistoryStore } from '@/feature/terminal_tables/account_history';
import { useTradeHistoryStore } from '@/feature/terminal_tables/trade_history/TradeHistoryStore.ts';
import { useNotificationStore } from '@/feature/notifications';
import { useAccountStatsStore } from '../features/portfolio/store/AccountStatsStore.ts';
import { usePnLHistoryStore } from '@/feature/terminal_tables/close_pnl/PnLHistoryStore.ts';

enableMapSet();

// The RootState combines all slice states
export type RootState = AuthSlice & LoadingSlice & AppConfigSlice & DexWalletSlice & MarketSlice;

const useStore = create<RootState>()(
  subscribeWithSelector(
    persist(
      devtools(
        immer((...args) => ({
          ...createAuthSlice(...args),
          ...createLoadingSlice(...args),
          ...createAppConfigSlice(...args),
          ...createWalletSlice(...args),
          ...createMarketSlice(...args),
        })),
        {
          name: 'dex-zustand-root',
          ...BigIntDevtoolsSerialize,
        }
      ),
      {
        name: 'dex-zustand-root',
        version: CURRENT_APP_VERSION,
        migrate: clearDataMigration,
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          authMethod: state.authMethod,
          authNonce: state.authNonce,
          termsAccepted: state.termsAccepted,
          inviteCode: state.inviteCode,
          referredBy: state.referredBy,
          hasShownWikImage: state.hasShownWikImage,
          summerBannerSeen: state.summerBannerSeen,
          millionBannerSeen: state.millionBannerSeen,
          favoriteMarkets: state.favoriteMarkets,
          favoritesDisplayMode: state.favoritesDisplayMode,
          lastRequestId: state.lastRequestId,
        }),
      }
    )
  )
);

installAuthExpirySupervisor(useStore);

// Meta-action to reset all account-related stores on logout
export const resetUserStores = () => {
  useStore.getState().resetWallet();
  useOpenOrdersStore.getState().reset();
  useTradeHistoryStore.getState().reset();
  useAccountHistoryStore.getState().reset();
  usePnLHistoryStore.getState().reset();
  useOrderHistoryStore.getState().reset();
  usePositionStore.getState().reset();
  useAccountStatsStore.getState().reset();
  useNotificationStore.getState().clear();
  useLiquidationAlertsStore.getState().clearState();
};

// Create a typed selectors
export const useAppConfigStore = () => {
  return useStore((state) => state as AppConfigSlice);
};

export const useAccountBalance = (): bigint | undefined => {
  return useStore((state) => {
    if (!state.account?.b) {
      return undefined;
    }
    const availableBalance = BigInt(state.account.b) - BigInt(state.account.lb || 0);
    return availableBalance < 0n ? 0n : availableBalance;
  });
};

export const useTotalAccountBalance = (): bigint => {
  return useStore((state) => BigInt(state.account?.b || 0));
};

export const useLockedBalance = (): bigint | undefined => {
  return useStore((state) => (state.account?.lb ? BigInt(state.account.lb) : undefined));
};

// Market selectors
export const useSelectedMarket = () => {
  return useStore((state) => (state.selectedMarketId ? state.markets.get(state.selectedMarketId) : undefined));
};

export const useSelectedMarketId = () => {
  return useStore((state) => state.selectedMarketId);
};

/** One field of the selected market. The market object's identity churns every tick; this doesn't. */
export const useSelectedMarketField = <K extends keyof MarketData>(field: K): MarketData[K] | undefined => {
  return useStore((state) => (state.selectedMarketId ? state.markets.get(state.selectedMarketId)?.[field] : undefined));
};

export const useMarkets = (): ReadonlyMap<number, MarketData> => {
  return useStore((state) => state.markets);
};

export const useActiveExchangeAddress = (): Address | undefined => {
  return useStore((state) => {
    const activeDelegatedAddress = state.activeDelegatedAddress;
    if (activeDelegatedAddress) return getAddress(activeDelegatedAddress);

    const exchange = state.exchange?.contract;
    if (exchange?.address) return getAddress(exchange.address);

    return undefined;
  });
};

export default useStore;
